import { DatePipe } from '@angular/common';
import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { map, Observable, of, Subject, takeUntil } from 'rxjs';
import { AccountDataService } from 'src/app/services/account-data.service';
import { RISK_ANALYTICS_ACCESS_TYPE, DILIGENT_SCREENING_VENDOR } from '../../constants';
import * as XRegExp from 'xregexp';
import '../../models/modal';
import '../../models/AccountMetaDatamodal';

const DEFAULT_SCREENING_VENDORS: AccessType[] = [
  { code: DILIGENT_SCREENING_VENDOR, description: 'DILIGENT' },
  { code: 'RPS', description: 'RPS' }
];

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss']
})
export class CreateAccountComponent implements OnInit, OnChanges, OnDestroy {
  WORDS_REGEX = XRegExp('^[\\p{L}\'\\s]*$'); // Allow any language ONLY letters 
  LOGIN_ENTITY_REGEX = '[a-zA-Z-.@()0-9]*';        // Allow english ONLY letter and hyphen
  ACCOUNT_NAME_REGEX = XRegExp('^[\\p{L}\\s\-,.\\\\\/()&0-9]*$');  // Special Case for Account Name
  PARTNER_NAME_REGEX = XRegExp('^[\\p{L}\\s\-,.\'()&]*$');  // Special Case for Partner Name 
  NUMBER_REGEX = '^[0-9]+$'; // Allow ONLY Numbers 
  NUMBER_LETTERS_REGEX = '^[0-9a-zA-Z-]+$'; // Allow ONLY Numbers 
  ZERO_TO_100_REGEX = '^(100(\.0{1,2})?|([0-9]?[0-9])?|([0-9]?[0-9](\.[0-9]{1,2})))$';
  public accountForm!: FormGroup;
  partners: Option[] = [];
  ownershipTypes: Option[] = [];
  countries: Country[] = [];
  accountTypes: AccountType[] = [];
  accessTypes: AccessType[] = [];
  screeningVendors: AccessType[] = [];
  screeningLevels: ScreeningLevelInput[] = [];
  timeZones: TimeZone[] = [];
  accountData!: AccountRequest;
  accountId: string = '';
  showAdminSection = false;
  showBusinessExpnSection = false;
  showCustomListSection = false;
  customListFileUploaded = false;
  selectedFile: File | null = null;
  isUpdating: boolean = false;
  loginEntity: string = '';
  adminEmail: string = '';
  isCustomListEnabled: boolean = false;
  isAdminEmailMessage = {
    message: "",
    isExists: false
  };
  headingText = 'Create New Account';
  partnerName = '';
  private unsubscribe$ = new Subject<void>();
  items: MenuItem[] = [
    { label: 'Account Details', routerLink: '/config/account/list' },
    { label: 'Create Account' }];
  defaultBusinessExpnValues = {
    ownershipType: 'BENF_OWRP',
    ownershipPercentage: '25',
    degreeOfSeparation: '1'
  };
  ownershipToolTip = "test tooltip <br/> abcd test ";
  fileUploadLabel = "";

  constructor(
    private formBuilder: FormBuilder,
    private accountService: AccountDataService,
    private messageService: MessageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.activatedRoute.data.
      pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.isUpdating = data['isUpdating'];
      });
    this.accountService.getAccountMetaData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result && result?.responseData) {
          const data: Response = result.responseData;
          this.countries = data.country;
          this.accessTypes = data.accessType;
          this.screeningVendors = this.getScreeningVendorOptions(data);
          this.accountTypes = data.accountType;
          this.screeningLevels = data.screeningLevels;
          this.ownershipTypes = data.ownershipTypes;
          this.timeZones = data.timeZone;
          if (this.isUpdating) {
            this.ifUpdateAccount();
          }
        }
      });
    this.accountService.getPartnersAccountList()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result && result?.responseData) {
          this.partners = result.responseData.partners;
        }
      });

    this.accountForm = this.formBuilder.group({
      accountName: new FormControl('', [Validators.required, Validators.maxLength(200), Validators.pattern(this.ACCOUNT_NAME_REGEX)]),
      partnerEnrollmentId: new FormControl({ value: '', disabled: this.isUpdating }),
      country: new FormControl('', [Validators.required]),
      accountType: new FormControl('', [Validators.required]),
      accessType: new FormControl('', [Validators.required, this.riskAnalyticsAccessTypeValidator()]),
      screeningVendor: new FormControl('', [Validators.required]),
      screeningLevel: new FormControl('', [Validators.required]),
      businessExpnEnabled: new FormControl('false', [Validators.required]),
      businessExpansionOptions: this.formBuilder.group({
        ownershipType: new FormControl(this.defaultBusinessExpnValues.ownershipType),
        ownershipPercentage: new FormControl(this.defaultBusinessExpnValues.ownershipPercentage),
        degreeOfSeparation: new FormControl(this.defaultBusinessExpnValues.degreeOfSeparation)
      }),
      suffixVariations: new FormControl(0, [Validators.required]),
      customListEnabled: new FormControl('false', [Validators.required]),
      monitoringEnabled: new FormControl('true', [Validators.required]),
      loginEntityId: new FormControl('', [Validators.required, Validators.maxLength(50), Validators.pattern(this.LOGIN_ENTITY_REGEX)]),
      timeZone: new FormControl('', [Validators.required]),
      adminFirstName: new FormControl(''),
      adminLastName: new FormControl(''),
      adminEmail: new FormControl(''),
      salesContactName: new FormControl('', [Validators.required, Validators.maxLength(50), Validators.pattern(this.WORDS_REGEX)]),
      salesContactEmail: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(100)]),
      contractStartDate: new FormControl('', [Validators.required]),
      contractEndDate: new FormControl('', [Validators.required]),
      dnbPartnerName: new FormControl('', [Validators.pattern(this.PARTNER_NAME_REGEX)]),
      subscriberNumber: new FormControl('', [Validators.pattern(this.NUMBER_LETTERS_REGEX)]),
      duns: new FormControl('', [Validators.pattern(this.NUMBER_REGEX)]),
      orderNumber: new FormControl('', [Validators.pattern(this.NUMBER_LETTERS_REGEX)])
    });
    this.accountForm.controls['loginEntityId'].setAsyncValidators([this.loginEntityValidator()]);
  }

  onaccessTypeChange(value: string): void {
    const isRiskAnalytics = value === RISK_ANALYTICS_ACCESS_TYPE;
    this.toggleFormForRiskAnalytics(isRiskAnalytics);

    const screeningVendorControl = this.accountForm.controls['screeningVendor'];
    // for access type RPSi and D+, screening vendor should be RPS
    if (!isRiskAnalytics && (value === 'RPSi' || value === 'D+')) {
      screeningVendorControl.setValue('RPS');
      screeningVendorControl.disable();
    } else if (!isRiskAnalytics) {
      screeningVendorControl.enable();
    }
    screeningVendorControl.updateValueAndValidity();

    if (value === 'RPSi') {
      this.showAdminSection = true;
      this.accountForm.controls['adminFirstName'].setValidators([Validators.required, Validators.maxLength(50), Validators.pattern(this.WORDS_REGEX)]);
      this.accountForm.controls['adminLastName'].setValidators([Validators.required, Validators.maxLength(50), Validators.pattern(this.WORDS_REGEX)]);
      this.accountForm.controls['adminEmail'].setValidators([Validators.required, Validators.maxLength(100), Validators.email,]);
    } else {
      this.showAdminSection = false;
      this.accountForm.controls['adminFirstName'].setValidators(null);
      this.accountForm.controls['adminLastName'].setValidators(null);
      this.accountForm.controls['adminEmail'].setValidators(null);
    }
    this.accountForm.controls['adminFirstName'].updateValueAndValidity();
    this.accountForm.controls['adminLastName'].updateValueAndValidity();
    this.accountForm.controls['adminEmail'].updateValueAndValidity();
  }

  toggleFormForRiskAnalytics(isRiskAnalytics: boolean): void {
    Object.keys(this.accountForm.controls).forEach((controlName) => {
      if (controlName === 'accessType') {
        return;
      }

      const control = this.accountForm.controls[controlName];
      if (isRiskAnalytics) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });

    if (!isRiskAnalytics && this.isUpdating) {
      this.accountForm.controls['partnerEnrollmentId'].disable({ emitEvent: false });
    }
  }

  onbusinessExpnChange(value: string): void {
    if (value === 'true') {
      this.showBusinessExpnSection = true;
      this.accountForm.get('businessExpansionOptions.ownershipPercentage')?.setValidators([Validators.pattern(this.ZERO_TO_100_REGEX), Validators.max(100)]);
      this.accountForm.get('businessExpansionOptions.degreeOfSeparation')?.setValidators([Validators.min(1)]);
    } else {
      this.showBusinessExpnSection = false;
      this.accountForm.get('businessExpansionOptions.ownershipPercentage')?.setValidators(null);
      this.accountForm.get('businessExpansionOptions.degreeOfSeparation')?.setValidators(null);
    }
    this.accountForm.get('businessExpansionOptions.ownershipPercentage')?.updateValueAndValidity();
    this.accountForm.get('businessExpansionOptions.degreeOfSeparation')?.updateValueAndValidity();
  }

  onCustomListChange(value: string): void {
    if (value === 'true') {
      this.showCustomListSection = true;
      if (this.customListFileUploaded === false) {
        this.accountForm.get('customListOptions.customListFile')?.setValidators([Validators.required]);
        this.accountForm.get('customListOptions.customListFileName')?.setValidators([Validators.required, Validators.maxLength(200), Validators.pattern(this.NUMBER_LETTERS_REGEX)]);
      }
    } else {
      this.showCustomListSection = false;
      this.accountForm.get('customListOptions.customListFile')?.setValidators(null);
      this.accountForm.get('customListOptions.customListFileName')?.setValidators(null);
    }
    this.accountForm.get('customListOptions.customListFileName')?.updateValueAndValidity();
    this.accountForm.get('customListOptions.customListFile')?.updateValueAndValidity();
  }

  onFileSelected(event: any): void {
    if (event.target.files.length > 0) {
      this.selectedFile = event.target.files[0];
      this.accountForm.get('customListOptions.customListFile')?.updateValueAndValidity();
      this.fileUploadLabel = this.accountForm.get('customListOptions.customListFileName')?.value;
    }
  }

  clearFrom(): void {
    if (this.accountForm) {
      for (const control of Object.keys(this.accountForm.controls)) {
        this.accountForm.controls[control].patchValue(null);
        this.accountForm.controls[control].markAsUntouched();
      }
    }
  }

  validate(): void {
    if (this.accountForm.invalid) {
      for (const control of Object.keys(this.accountForm.controls)) {
        this.accountForm.controls[control].markAsTouched();
      }
      return;
    }
    let dateFormat = new DatePipe('en-US');
    let contractStartDate = dateFormat.transform(this.accountForm.controls['contractStartDate'].value, 'yyyy-MM-dd') || '';
    let contractEndDate = dateFormat.transform(this.accountForm.controls['contractEndDate'].value, 'yyyy-MM-dd') || '';
    let partnerId = this.accountForm.controls['partnerEnrollmentId'].value;
    let accountBody: AccountData = {
      setupDetails: {
        accountName: this.accountForm.controls['accountName'].value,
        partnerEnrollmentId: partnerId,
        partnerName: (this.partners.find(e => e.code === partnerId))?.code,
        country: this.accountForm.controls['country'].value,
        accountType: this.accountForm.controls['accountType'].value,
        accessType: this.accountForm.controls['accessType'].value,
        screeningVendor: this.accountForm.controls['screeningVendor'].value,
        screeningLevel: this.accountForm.controls['screeningLevel'].value,
        businessExpnEnabled: this.accountForm.controls['businessExpnEnabled'].value,
        businessExpansionOptions: {
          ownershipType: this.accountForm.get('businessExpansionOptions.ownershipType')?.value,
          ownershipPercentage: this.accountForm.get('businessExpansionOptions.ownershipPercentage')?.value,
          degreeOfSeparation: this.accountForm.get('businessExpansionOptions.degreeOfSeparation')?.value
        },
        suffixVariations: this.accountForm.controls['suffixVariations'].value,
        customListEnabled: this.accountForm.get('customListEnabled')?.value,
        monitoringEnabled: this.accountForm.controls['monitoringEnabled'].value,
        loginEntityId: this.accountForm.controls['loginEntityId'].value,
        timeZone: this.accountForm.controls['timeZone'].value,
        adminFirstName: this.accountForm.controls['adminFirstName'].value,
        adminLastName: this.accountForm.controls['adminLastName'].value,
        adminEmail: this.accountForm.controls['adminEmail'].value
      },
      administrativeDetails: {
        salesContactName: this.accountForm.controls['salesContactName'].value,
        salesContactEmail: this.accountForm.controls['salesContactEmail'].value,
        subscriberNumber: this.accountForm.controls['subscriberNumber'].value,
        contractStartDate: contractStartDate,
        contractEndDate: contractEndDate,
        duns: this.accountForm.controls['duns'].value,
        orderNumber: this.accountForm.controls['orderNumber'].value,
        dnbPartnerName: this.accountForm.controls['dnbPartnerName'].value
      }
    }
    if(this.isCustomListEnabled == false && accountBody.setupDetails.customListEnabled == 'true') { 
      this.isCustomListEnabled = true;
    } else {
      this.isCustomListEnabled = false;
    }

    if (this.isUpdating) {
      delete accountBody.setupDetails.partnerEnrollmentId;
      delete accountBody.setupDetails.partnerName;
      
      this.updateAccountData(accountBody);
    } else {
      this.createAccountData(accountBody);
    }
  }

  ifUpdateAccount() {
    this.activatedRoute.paramMap.subscribe(param => {
      const category = param.get('accountId')
      if (category) {
        this.getAccountById(category);
        this.headingText = 'Update Account';
      }
    })
  }

  getAccountById(id: string) {
    this.accountService.getAccountById(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result && result?.responseData) {
          this.setDataToAccountFrom(result?.responseData.accountResponse);
        }
      });
  }

  setDataToAccountFrom(accountData: AccountData) {
    let accountFormData: IAccount;
    this.accountId = accountData.accountId || '';
    let setupDetails = accountData.setupDetails;
    accountData.setupDetails.accessType = accountData.setupDetails.accessType || '';
    accountData.setupDetails.screeningVendor = accountData.setupDetails.screeningVendor || '';
    let administrativeDetails = accountData.administrativeDetails;
    let accountObj = this.accountTypes.find(obj => obj.description === accountData.setupDetails.accountType)
    let accountType = accountObj?.code;
    this.loginEntity = setupDetails.loginEntityId || '';
    this.adminEmail = setupDetails.adminEmail || '';
    accountFormData = { ...setupDetails, ...administrativeDetails };
    if (setupDetails.customListEnabled+'' == 'true')
    {  this.isCustomListEnabled = true;
    }else{
      this.isCustomListEnabled = false;}
    this.onaccessTypeChange(setupDetails.accessType);
    this.onbusinessExpnChange(setupDetails.businessExpnEnabled + '');
    
    accountFormData.contractStartDate = this.dateTransform(accountFormData.contractStartDate);
    accountFormData.contractEndDate = this.dateTransform(accountFormData.contractEndDate);
    this.accountForm.patchValue(accountFormData);
    this.onaccessTypeChange(this.accountForm.controls['accessType'].value);
    this.accountForm.controls['accountType'].patchValue(accountType);
    this.accountForm.controls['businessExpnEnabled'].patchValue(accountFormData.businessExpnEnabled + '');
    this.accountForm.controls['customListEnabled']?.patchValue(accountFormData?.customListEnabled + '');
    this.accountForm.controls['monitoringEnabled'].patchValue(accountFormData.monitoringEnabled + '');
    this.accountForm.controls['screeningLevel'].patchValue((accountFormData.screeningLevel || '') + '');
    this.partnerName = accountData.setupDetails.partnerName || '';
    this.accountForm.controls['adminEmail'].setAsyncValidators([this.adminEmailValidator()]);
    this.fileUploadLabel = this.accountForm.get('customListOptions.customListFileName')?.value;
  }

  dateTransform(value: string) {
    if (!value || value == "")
      return "";
    const [year, month, day] = value.split('-');
    return [month, day, year].join('/');
  }

  createAccountData(accountBody: AccountData) {
    this.accountService.createAccount(accountBody)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.messageService.add({ severity: 'success', life: 10000, summary: 'Success', detail: result?.responseData?.message });
        if(this.isCustomListEnabled)
          this.router.navigate(['/config/account', result?.responseData?.accountId, 'view', 'true']);
        else 
          this.router.navigate(['/config/account', result?.responseData?.accountId, 'view']);
      });
  }

  updateAccountData(accountBody: AccountData) {
    this.accountService.updateAccount(accountBody, this.accountId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.messageService.add({ severity: 'success', life: 10000, summary: 'Success', detail: result?.responseData?.message });
         if(this.isCustomListEnabled)
          this.router.navigate(['/config/account', result?.responseData?.accountId, 'view', 'true']);
        else 
          this.router.navigate(['/config/account', result?.responseData?.accountId, 'view']);
      });
  }

  loginEntityValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      // if account is in edit mode then loginEntityID will be checked only change happening.
      if (this.isUpdating && control.value === this.loginEntity)
        return of(null);
      else
        return this.accountService.checkIfloginEntityExists(control.value).pipe(
          map(res => {
            // if loginEntityExists is true, loginEntity exists, return true
            return res.responseData?.loginEntityExists ? { loginEntityExists: true } : null;
            // NB: Return null if there is no error
          })
        );
    };
  }

  riskAnalyticsAccessTypeValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '').toString().trim().toLowerCase();
      const isRiskAnalyticsSelected = value === RISK_ANALYTICS_ACCESS_TYPE.toLowerCase();
      return isRiskAnalyticsSelected ? { riskAnalyticsNotAllowed: true } : null;
    };
  }

  getScreeningVendorOptions(data: Response): AccessType[] {
    const vendors = data?.screeningVendor ?? data?.screeningVendors;
    if (!Array.isArray(vendors) || vendors.length === 0) {
      return [...DEFAULT_SCREENING_VENDORS];
    }

    const mappedVendors = vendors
      .map((vendor: any) => {
        if (typeof vendor === 'string') {
          return { code: vendor, description: vendor };
        }

        const code = vendor?.code || vendor?.description;
        const description = vendor?.description || vendor?.code;
        if (!code || !description) {
          return null;
        }

        return { code, description };
      })
      .filter((vendor: AccessType | null): vendor is AccessType => vendor !== null);

    return mappedVendors.length ? mappedVendors : [...DEFAULT_SCREENING_VENDORS];
  }

  adminEmailValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      // if account is in edit mode then Admin Email will be checked only change happening.
      if (this.isUpdating && control.value === this.adminEmail) {
        this.isAdminEmailMessage.isExists = false;
        return of(null);
      }
      else
        return this.accountService.checkIfAdminEmailExists(control.value, this.accountId).pipe(
          map(res => {
            // if loginEntityExists is true, loginEntity exists, return true
            this.isAdminEmailMessage.isExists = res.responseData?.adminEmailExists ? true : false;
            this.isAdminEmailMessage.message = res.responseData?.message;
            return null;
            // NB: Return null if there is no error
          })
        );
    };
  }

  onDragOver(event: any) {
    event.preventDefault();
  }

  onDropSuccess(event: any) {
    event.preventDefault();
    this.onFileSelected(event.dataTransfer.files);
  }

  ngOnChanges() {
    this.clearFrom();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
